/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `split` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class SplitMesg : Mesg {
    public constructor() : super("Split", Profile.MesgNum.SPLIT)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** message_index. */
    public var messageIndex: UShort?
        get() = getFieldValue(MESSAGE_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MESSAGE_INDEX_FIELD_NUM, value)

    /** timestamp, in s. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** split_type. */
    public var splitType: SplitType?
        get() = (getFieldValue(SPLIT_TYPE_FIELD_NUM) as? UByte)?.let { SplitType.fromValue(it) }
        set(value) = setFieldValue(SPLIT_TYPE_FIELD_NUM, value?.value)

    /** total_elapsed_time, in s. */
    public var totalElapsedTime: Double?
        get() = getFieldValue(TOTAL_ELAPSED_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_ELAPSED_TIME_FIELD_NUM, value)

    /** total_timer_time, in s. */
    public var totalTimerTime: Double?
        get() = getFieldValue(TOTAL_TIMER_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_TIMER_TIME_FIELD_NUM, value)

    /** total_distance, in m. */
    public var totalDistance: Double?
        get() = getFieldValue(TOTAL_DISTANCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_DISTANCE_FIELD_NUM, value)

    /** avg_speed, in m/s. */
    public var avgSpeed: Double?
        get() = getFieldValue(AVG_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_SPEED_FIELD_NUM, value)

    /** start_time. */
    public var startTime: Instant?
        get() = (getFieldValue(START_TIME_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(START_TIME_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** sport. */
    public var sport: Sport?
        get() = (getFieldValue(SPORT_FIELD_NUM) as? UByte)?.let { Sport.fromValue(it) }
        set(value) = setFieldValue(SPORT_FIELD_NUM, value?.value)

    /** sub_sport. */
    public var subSport: SubSport?
        get() = (getFieldValue(SUB_SPORT_FIELD_NUM) as? UByte)?.let { SubSport.fromValue(it) }
        set(value) = setFieldValue(SUB_SPORT_FIELD_NUM, value?.value)

    /** total_ascent, in m. */
    public var totalAscent: UShort?
        get() = getFieldValue(TOTAL_ASCENT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TOTAL_ASCENT_FIELD_NUM, value)

    /** total_descent, in m. */
    public var totalDescent: UShort?
        get() = getFieldValue(TOTAL_DESCENT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TOTAL_DESCENT_FIELD_NUM, value)

    /** avg_heart_rate, in bpm. */
    public var avgHeartRate: UByte?
        get() = getFieldValue(AVG_HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(AVG_HEART_RATE_FIELD_NUM, value)

    /** max_heart_rate, in bpm. */
    public var maxHeartRate: UByte?
        get() = getFieldValue(MAX_HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(MAX_HEART_RATE_FIELD_NUM, value)

    /** nec_lat, in semicircles. North east corner latitude */
    public var necLat: Int?
        get() = getFieldValue(NEC_LAT_FIELD_NUM) as? Int
        set(value) = setFieldValue(NEC_LAT_FIELD_NUM, value)

    /** nec_long, in semicircles. North east corner longitude */
    public var necLong: Int?
        get() = getFieldValue(NEC_LONG_FIELD_NUM) as? Int
        set(value) = setFieldValue(NEC_LONG_FIELD_NUM, value)

    /** swc_lat, in semicircles. South west corner latitude */
    public var swcLat: Int?
        get() = getFieldValue(SWC_LAT_FIELD_NUM) as? Int
        set(value) = setFieldValue(SWC_LAT_FIELD_NUM, value)

    /** swc_long, in semicircles. South west corner longitude */
    public var swcLong: Int?
        get() = getFieldValue(SWC_LONG_FIELD_NUM) as? Int
        set(value) = setFieldValue(SWC_LONG_FIELD_NUM, value)

    /** start_position_lat, in semicircles. */
    public var startPositionLat: Int?
        get() = getFieldValue(START_POSITION_LAT_FIELD_NUM) as? Int
        set(value) = setFieldValue(START_POSITION_LAT_FIELD_NUM, value)

    /** start_position_long, in semicircles. */
    public var startPositionLong: Int?
        get() = getFieldValue(START_POSITION_LONG_FIELD_NUM) as? Int
        set(value) = setFieldValue(START_POSITION_LONG_FIELD_NUM, value)

    /** end_position_lat, in semicircles. */
    public var endPositionLat: Int?
        get() = getFieldValue(END_POSITION_LAT_FIELD_NUM) as? Int
        set(value) = setFieldValue(END_POSITION_LAT_FIELD_NUM, value)

    /** end_position_long, in semicircles. */
    public var endPositionLong: Int?
        get() = getFieldValue(END_POSITION_LONG_FIELD_NUM) as? Int
        set(value) = setFieldValue(END_POSITION_LONG_FIELD_NUM, value)

    /** max_speed, in m/s. */
    public var maxSpeed: Double?
        get() = getFieldValue(MAX_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_SPEED_FIELD_NUM, value)

    /** avg_vert_speed, in m/s. */
    public var avgVertSpeed: Double?
        get() = getFieldValue(AVG_VERT_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_VERT_SPEED_FIELD_NUM, value)

    /** end_time. */
    public var endTime: Instant?
        get() = (getFieldValue(END_TIME_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(END_TIME_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** total_calories, in kcal. */
    public var totalCalories: UInt?
        get() = getFieldValue(TOTAL_CALORIES_FIELD_NUM) as? UInt
        set(value) = setFieldValue(TOTAL_CALORIES_FIELD_NUM, value)

    /** avg_cadence, in rpm. */
    public var avgCadence: Double?
        get() = getFieldValue(AVG_CADENCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_CADENCE_FIELD_NUM, value)

    /**
     * avg_running_cadence: what avg_cadence means when this message selects it.
     */
    public val avgRunningCadence: Double?
        get() = getFieldValue(AVG_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("AvgRunningCadence")) as? Double

    public fun setAvgRunningCadence(value: Double?) {
        setFieldValue(AVG_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("AvgRunningCadence"))
    }

    /**
     * avg_swimming_cadence: what avg_cadence means when this message selects it.
     */
    public val avgSwimmingCadence: Double?
        get() = getFieldValue(AVG_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("AvgSwimmingCadence")) as? Double

    public fun setAvgSwimmingCadence(value: Double?) {
        setFieldValue(AVG_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("AvgSwimmingCadence"))
    }

    /**
     * avg_paddlesport_cadence: what avg_cadence means when this message selects it.
     */
    public val avgPaddlesportCadence: Double?
        get() = getFieldValue(AVG_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("AvgPaddlesportCadence")) as? Double

    public fun setAvgPaddlesportCadence(value: Double?) {
        setFieldValue(AVG_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("AvgPaddlesportCadence"))
    }

    /**
     * avg_push_cadence: what avg_cadence means when this message selects it.
     *
     * average pushes per minute in a split
     */
    public val avgPushCadence: UShort?
        get() = getFieldValue(AVG_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("AvgPushCadence")) as? UShort

    public fun setAvgPushCadence(value: UShort?) {
        setFieldValue(AVG_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("AvgPushCadence"))
    }

    /** max_cadence, in rpm. */
    public var maxCadence: Double?
        get() = getFieldValue(MAX_CADENCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_CADENCE_FIELD_NUM, value)

    /**
     * max_running_cadence: what max_cadence means when this message selects it.
     */
    public val maxRunningCadence: Double?
        get() = getFieldValue(MAX_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("MaxRunningCadence")) as? Double

    public fun setMaxRunningCadence(value: Double?) {
        setFieldValue(MAX_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("MaxRunningCadence"))
    }

    /**
     * max_swimming_cadence: what max_cadence means when this message selects it.
     */
    public val maxSwimmingCadence: Double?
        get() = getFieldValue(MAX_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("MaxSwimmingCadence")) as? Double

    public fun setMaxSwimmingCadence(value: Double?) {
        setFieldValue(MAX_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("MaxSwimmingCadence"))
    }

    /**
     * max_paddlesport_cadence: what max_cadence means when this message selects it.
     */
    public val maxPaddlesportCadence: Double?
        get() = getFieldValue(MAX_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("MaxPaddlesportCadence")) as? Double

    public fun setMaxPaddlesportCadence(value: Double?) {
        setFieldValue(MAX_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("MaxPaddlesportCadence"))
    }

    /**
     * max_push_cadence: what max_cadence means when this message selects it.
     *
     * max pushes per minute in a split
     */
    public val maxPushCadence: UShort?
        get() = getFieldValue(MAX_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("MaxPushCadence")) as? UShort

    public fun setMaxPushCadence(value: UShort?) {
        setFieldValue(MAX_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("MaxPushCadence"))
    }

    /** total_cycles, in cycles. */
    public var totalCycles: UInt?
        get() = getFieldValue(TOTAL_CYCLES_FIELD_NUM) as? UInt
        set(value) = setFieldValue(TOTAL_CYCLES_FIELD_NUM, value)

    /**
     * total_strides: what total_cycles means when this message selects it.
     */
    public val totalStrides: UInt?
        get() = getFieldValue(TOTAL_CYCLES_FIELD_NUM, 0, SubFieldSelector.Named("TotalStrides")) as? UInt

    public fun setTotalStrides(value: UInt?) {
        setFieldValue(TOTAL_CYCLES_FIELD_NUM, value, 0, SubFieldSelector.Named("TotalStrides"))
    }

    /**
     * total_strokes: what total_cycles means when this message selects it.
     */
    public val totalStrokes: UInt?
        get() = getFieldValue(TOTAL_CYCLES_FIELD_NUM, 0, SubFieldSelector.Named("TotalStrokes")) as? UInt

    public fun setTotalStrokes(value: UInt?) {
        setFieldValue(TOTAL_CYCLES_FIELD_NUM, value, 0, SubFieldSelector.Named("TotalStrokes"))
    }

    /**
     * total_reps: what total_cycles means when this message selects it.
     *
     * total reps in a split round
     */
    public val totalReps: UInt?
        get() = getFieldValue(TOTAL_CYCLES_FIELD_NUM, 0, SubFieldSelector.Named("TotalReps")) as? UInt

    public fun setTotalReps(value: UInt?) {
        setFieldValue(TOTAL_CYCLES_FIELD_NUM, value, 0, SubFieldSelector.Named("TotalReps"))
    }

    /**
     * total_pushes: what total_cycles means when this message selects it.
     *
     * total number of pushes in a split
     */
    public val totalPushes: UInt?
        get() = getFieldValue(TOTAL_CYCLES_FIELD_NUM, 0, SubFieldSelector.Named("TotalPushes")) as? UInt

    public fun setTotalPushes(value: UInt?) {
        setFieldValue(TOTAL_CYCLES_FIELD_NUM, value, 0, SubFieldSelector.Named("TotalPushes"))
    }

    /** avg_temperature, in C. */
    public var avgTemperature: Byte?
        get() = getFieldValue(AVG_TEMPERATURE_FIELD_NUM) as? Byte
        set(value) = setFieldValue(AVG_TEMPERATURE_FIELD_NUM, value)

    /** max_temperature, in C. */
    public var maxTemperature: Byte?
        get() = getFieldValue(MAX_TEMPERATURE_FIELD_NUM) as? Byte
        set(value) = setFieldValue(MAX_TEMPERATURE_FIELD_NUM, value)

    /** min_temperature, in C. */
    public var minTemperature: Byte?
        get() = getFieldValue(MIN_TEMPERATURE_FIELD_NUM) as? Byte
        set(value) = setFieldValue(MIN_TEMPERATURE_FIELD_NUM, value)

    /** avg_vertical_oscillation, in mm. */
    public var avgVerticalOscillation: Double?
        get() = getFieldValue(AVG_VERTICAL_OSCILLATION_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_VERTICAL_OSCILLATION_FIELD_NUM, value)

    /** avg_vertical_ratio, in percent. */
    public var avgVerticalRatio: Double?
        get() = getFieldValue(AVG_VERTICAL_RATIO_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_VERTICAL_RATIO_FIELD_NUM, value)

    /** avg_stance_time, in ms. */
    public var avgStanceTime: Double?
        get() = getFieldValue(AVG_STANCE_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_STANCE_TIME_FIELD_NUM, value)

    /** avg_stance_time_balance, in percent. */
    public var avgStanceTimeBalance: Double?
        get() = getFieldValue(AVG_STANCE_TIME_BALANCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_STANCE_TIME_BALANCE_FIELD_NUM, value)

    /** avg_step_length, in mm. */
    public var avgStepLength: Double?
        get() = getFieldValue(AVG_STEP_LENGTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_STEP_LENGTH_FIELD_NUM, value)

    /** avg_power, in watts. */
    public var avgPower: UShort?
        get() = getFieldValue(AVG_POWER_FIELD_NUM) as? UShort
        set(value) = setFieldValue(AVG_POWER_FIELD_NUM, value)

    /** max_power, in watts. */
    public var maxPower: UShort?
        get() = getFieldValue(MAX_POWER_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MAX_POWER_FIELD_NUM, value)

    /** normalized_power, in watts. */
    public var normalizedPower: UShort?
        get() = getFieldValue(NORMALIZED_POWER_FIELD_NUM) as? UShort
        set(value) = setFieldValue(NORMALIZED_POWER_FIELD_NUM, value)

    /** left_right_balance. */
    public var leftRightBalance: UShort?
        get() = getFieldValue(LEFT_RIGHT_BALANCE_FIELD_NUM) as? UShort
        set(value) = setFieldValue(LEFT_RIGHT_BALANCE_FIELD_NUM, value)

    /** time_standing, in s. Total time spent in the standing position */
    public var timeStanding: Double?
        get() = getFieldValue(TIME_STANDING_FIELD_NUM) as? Double
        set(value) = setFieldValue(TIME_STANDING_FIELD_NUM, value)

    /** avg_left_pco, in mm. Average left platform center offset */
    public var avgLeftPco: Byte?
        get() = getFieldValue(AVG_LEFT_PCO_FIELD_NUM) as? Byte
        set(value) = setFieldValue(AVG_LEFT_PCO_FIELD_NUM, value)

    /** avg_right_pco, in mm. Average right platform center offset */
    public var avgRightPco: Byte?
        get() = getFieldValue(AVG_RIGHT_PCO_FIELD_NUM) as? Byte
        set(value) = setFieldValue(AVG_RIGHT_PCO_FIELD_NUM, value)

    /** Number of values stored in avg_left_power_phase. */
    public val numAvgLeftPowerPhase: Int
        get() = getField(AVG_LEFT_POWER_PHASE_FIELD_NUM)?.numValues ?: 0

    /** avg_left_power_phase, in degrees. Average left power phase angles. Data value indexes defined by power_phase_type. */
    public var avgLeftPowerPhase: List<Double?>?
        get() {
            val field = getField(AVG_LEFT_POWER_PHASE_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgLeftPowerPhase(it) }
        }
        set(value) {
            removeField(AVG_LEFT_POWER_PHASE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgLeftPowerPhase(index, element) }
        }

    public fun getAvgLeftPowerPhase(index: Int): Double? =
        getFieldValue(AVG_LEFT_POWER_PHASE_FIELD_NUM, index) as? Double

    public fun setAvgLeftPowerPhase(index: Int, value: Double?) {
        setFieldValue(AVG_LEFT_POWER_PHASE_FIELD_NUM, value, index)
    }

    /** Number of values stored in avg_left_power_phase_peak. */
    public val numAvgLeftPowerPhasePeak: Int
        get() = getField(AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM)?.numValues ?: 0

    /** avg_left_power_phase_peak, in degrees. Average left power phase peak angles. Data value indexes defined by power_phase_type. */
    public var avgLeftPowerPhasePeak: List<Double?>?
        get() {
            val field = getField(AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgLeftPowerPhasePeak(it) }
        }
        set(value) {
            removeField(AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgLeftPowerPhasePeak(index, element) }
        }

    public fun getAvgLeftPowerPhasePeak(index: Int): Double? =
        getFieldValue(AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM, index) as? Double

    public fun setAvgLeftPowerPhasePeak(index: Int, value: Double?) {
        setFieldValue(AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM, value, index)
    }

    /** Number of values stored in avg_right_power_phase. */
    public val numAvgRightPowerPhase: Int
        get() = getField(AVG_RIGHT_POWER_PHASE_FIELD_NUM)?.numValues ?: 0

    /** avg_right_power_phase, in degrees. Average right power phase angles. Data value indexes defined by power_phase_type. */
    public var avgRightPowerPhase: List<Double?>?
        get() {
            val field = getField(AVG_RIGHT_POWER_PHASE_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgRightPowerPhase(it) }
        }
        set(value) {
            removeField(AVG_RIGHT_POWER_PHASE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgRightPowerPhase(index, element) }
        }

    public fun getAvgRightPowerPhase(index: Int): Double? =
        getFieldValue(AVG_RIGHT_POWER_PHASE_FIELD_NUM, index) as? Double

    public fun setAvgRightPowerPhase(index: Int, value: Double?) {
        setFieldValue(AVG_RIGHT_POWER_PHASE_FIELD_NUM, value, index)
    }

    /** Number of values stored in avg_right_power_phase_peak. */
    public val numAvgRightPowerPhasePeak: Int
        get() = getField(AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM)?.numValues ?: 0

    /** avg_right_power_phase_peak, in degrees. Average right power phase peak angles. Data value indexes defined by power_phase_type. */
    public var avgRightPowerPhasePeak: List<Double?>?
        get() {
            val field = getField(AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgRightPowerPhasePeak(it) }
        }
        set(value) {
            removeField(AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgRightPowerPhasePeak(index, element) }
        }

    public fun getAvgRightPowerPhasePeak(index: Int): Double? =
        getFieldValue(AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM, index) as? Double

    public fun setAvgRightPowerPhasePeak(index: Int, value: Double?) {
        setFieldValue(AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM, value, index)
    }

    /** Number of values stored in avg_power_position. */
    public val numAvgPowerPosition: Int
        get() = getField(AVG_POWER_POSITION_FIELD_NUM)?.numValues ?: 0

    /** avg_power_position, in watts. Average power by position. Data value indexes defined by rider_position_type. */
    public var avgPowerPosition: List<UShort?>?
        get() {
            val field = getField(AVG_POWER_POSITION_FIELD_NUM) ?: return null
            return List(field.numValues) { getAvgPowerPosition(it) }
        }
        set(value) {
            removeField(AVG_POWER_POSITION_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAvgPowerPosition(index, element) }
        }

    public fun getAvgPowerPosition(index: Int): UShort? =
        getFieldValue(AVG_POWER_POSITION_FIELD_NUM, index) as? UShort

    public fun setAvgPowerPosition(index: Int, value: UShort?) {
        setFieldValue(AVG_POWER_POSITION_FIELD_NUM, value, index)
    }

    /** Number of values stored in max_power_position. */
    public val numMaxPowerPosition: Int
        get() = getField(MAX_POWER_POSITION_FIELD_NUM)?.numValues ?: 0

    /** max_power_position, in watts. Maximum power by position. Data value indexes defined by rider_position_type. */
    public var maxPowerPosition: List<UShort?>?
        get() {
            val field = getField(MAX_POWER_POSITION_FIELD_NUM) ?: return null
            return List(field.numValues) { getMaxPowerPosition(it) }
        }
        set(value) {
            removeField(MAX_POWER_POSITION_FIELD_NUM)
            value?.forEachIndexed { index, element -> setMaxPowerPosition(index, element) }
        }

    public fun getMaxPowerPosition(index: Int): UShort? =
        getFieldValue(MAX_POWER_POSITION_FIELD_NUM, index) as? UShort

    public fun setMaxPowerPosition(index: Int, value: UShort?) {
        setFieldValue(MAX_POWER_POSITION_FIELD_NUM, value, index)
    }

    /** avg_left_torque_effectiveness, in percent. */
    public var avgLeftTorqueEffectiveness: Double?
        get() = getFieldValue(AVG_LEFT_TORQUE_EFFECTIVENESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_LEFT_TORQUE_EFFECTIVENESS_FIELD_NUM, value)

    /** avg_right_torque_effectiveness, in percent. */
    public var avgRightTorqueEffectiveness: Double?
        get() = getFieldValue(AVG_RIGHT_TORQUE_EFFECTIVENESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_RIGHT_TORQUE_EFFECTIVENESS_FIELD_NUM, value)

    /** avg_left_pedal_smoothness, in percent. */
    public var avgLeftPedalSmoothness: Double?
        get() = getFieldValue(AVG_LEFT_PEDAL_SMOOTHNESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_LEFT_PEDAL_SMOOTHNESS_FIELD_NUM, value)

    /** avg_right_pedal_smoothness, in percent. */
    public var avgRightPedalSmoothness: Double?
        get() = getFieldValue(AVG_RIGHT_PEDAL_SMOOTHNESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_RIGHT_PEDAL_SMOOTHNESS_FIELD_NUM, value)

    /** avg_combined_pedal_smoothness, in percent. */
    public var avgCombinedPedalSmoothness: Double?
        get() = getFieldValue(AVG_COMBINED_PEDAL_SMOOTHNESS_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_COMBINED_PEDAL_SMOOTHNESS_FIELD_NUM, value)

    /** avg_flow, in Flow. The flow score estimates how long distance wise a cyclist deaccelerates over intervals where deacceleration is unnecessary such as smooth turns or small grade angle intervals. */
    public var avgFlow: Float?
        get() = getFieldValue(AVG_FLOW_FIELD_NUM) as? Float
        set(value) = setFieldValue(AVG_FLOW_FIELD_NUM, value)

    /** total_grit, in kGrit. The grit score estimates how challenging a route could be for a cyclist in terms of time spent going over sharp turns or large grade slopes. */
    public var totalGrit: Float?
        get() = getFieldValue(TOTAL_GRIT_FIELD_NUM) as? Float
        set(value) = setFieldValue(TOTAL_GRIT_FIELD_NUM, value)

    /** swim_stroke. */
    public var swimStroke: SwimStroke?
        get() = (getFieldValue(SWIM_STROKE_FIELD_NUM) as? UByte)?.let { SwimStroke.fromValue(it) }
        set(value) = setFieldValue(SWIM_STROKE_FIELD_NUM, value?.value)

    /** num_active_lengths, in lengths. # of active lengths of swim pool */
    public var numActiveLengths: UShort?
        get() = getFieldValue(NUM_ACTIVE_LENGTHS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(NUM_ACTIVE_LENGTHS_FIELD_NUM, value)

    /** avg_swolf. */
    public var avgSwolf: UShort?
        get() = getFieldValue(AVG_SWOLF_FIELD_NUM) as? UShort
        set(value) = setFieldValue(AVG_SWOLF_FIELD_NUM, value)

    /** avg_stroke_distance, in m. */
    public var avgStrokeDistance: Double?
        get() = getFieldValue(AVG_STROKE_DISTANCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_STROKE_DISTANCE_FIELD_NUM, value)

    /** avg_strokes_per_length, in strokes / length. */
    public var avgStrokesPerLength: Double?
        get() = getFieldValue(AVG_STROKES_PER_LENGTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_STROKES_PER_LENGTH_FIELD_NUM, value)

    /** first_lap_index. This is only set for split types that will trigger the end of a lap when the split ends */
    public var firstLapIndex: UShort?
        get() = getFieldValue(FIRST_LAP_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(FIRST_LAP_INDEX_FIELD_NUM, value)

    /** num_laps. This is only set for split types that will trigger the end of a lap when the split ends */
    public var numLaps: UShort?
        get() = getFieldValue(NUM_LAPS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(NUM_LAPS_FIELD_NUM, value)

    /** climb_grading_scale. */
    public var climbGradingScale: ClimbGradingScale?
        get() = (getFieldValue(CLIMB_GRADING_SCALE_FIELD_NUM) as? UByte)?.let { ClimbGradingScale.fromValue(it) }
        set(value) = setFieldValue(CLIMB_GRADING_SCALE_FIELD_NUM, value?.value)

    /** climb_grade_value. */
    public var climbGradeValue: UInt?
        get() = getFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM) as? UInt
        set(value) = setFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, value)

    /**
     * climb_grade_yds: what climb_grade_value means when this message selects it.
     */
    public val climbGradeYds: YdsGradingScale?
        get() = (getFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbGradeYds")) as? UByte)?.let { YdsGradingScale.fromValue(it) }

    public fun setClimbGradeYds(value: YdsGradingScale?) {
        setFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbGradeYds"))
    }

    /**
     * climb_grade_uiaa: what climb_grade_value means when this message selects it.
     */
    public val climbGradeUiaa: UiaaGradingScale?
        get() = (getFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbGradeUiaa")) as? UByte)?.let { UiaaGradingScale.fromValue(it) }

    public fun setClimbGradeUiaa(value: UiaaGradingScale?) {
        setFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbGradeUiaa"))
    }

    /**
     * climb_grade_french: what climb_grade_value means when this message selects it.
     */
    public val climbGradeFrench: FrenchGradingScale?
        get() = (getFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbGradeFrench")) as? UByte)?.let { FrenchGradingScale.fromValue(it) }

    public fun setClimbGradeFrench(value: FrenchGradingScale?) {
        setFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbGradeFrench"))
    }

    /**
     * climb_grade_british_adjectival: what climb_grade_value means when this message selects it.
     */
    public val climbGradeBritishAdjectival: BritishAdjectivalGradingScale?
        get() = (getFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbGradeBritishAdjectival")) as? UByte)?.let { BritishAdjectivalGradingScale.fromValue(it) }

    public fun setClimbGradeBritishAdjectival(value: BritishAdjectivalGradingScale?) {
        setFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbGradeBritishAdjectival"))
    }

    /**
     * climb_grade_british_technical: what climb_grade_value means when this message selects it.
     */
    public val climbGradeBritishTechnical: BritishTechnicalGradingScale?
        get() = (getFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbGradeBritishTechnical")) as? UByte)?.let { BritishTechnicalGradingScale.fromValue(it) }

    public fun setClimbGradeBritishTechnical(value: BritishTechnicalGradingScale?) {
        setFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbGradeBritishTechnical"))
    }

    /**
     * climb_grade_ewbank: what climb_grade_value means when this message selects it.
     */
    public val climbGradeEwbank: EwbankGradingScale?
        get() = (getFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbGradeEwbank")) as? UByte)?.let { EwbankGradingScale.fromValue(it) }

    public fun setClimbGradeEwbank(value: EwbankGradingScale?) {
        setFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbGradeEwbank"))
    }

    /**
     * climb_grade_brazilian: what climb_grade_value means when this message selects it.
     */
    public val climbGradeBrazilian: BrazilianGradingScale?
        get() = (getFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbGradeBrazilian")) as? UByte)?.let { BrazilianGradingScale.fromValue(it) }

    public fun setClimbGradeBrazilian(value: BrazilianGradingScale?) {
        setFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbGradeBrazilian"))
    }

    /**
     * climb_grade_saxon: what climb_grade_value means when this message selects it.
     */
    public val climbGradeSaxon: SaxonGradingScale?
        get() = (getFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbGradeSaxon")) as? UByte)?.let { SaxonGradingScale.fromValue(it) }

    public fun setClimbGradeSaxon(value: SaxonGradingScale?) {
        setFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbGradeSaxon"))
    }

    /**
     * climb_grade_vermin: what climb_grade_value means when this message selects it.
     */
    public val climbGradeVermin: VerminGradingScale?
        get() = (getFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbGradeVermin")) as? UByte)?.let { VerminGradingScale.fromValue(it) }

    public fun setClimbGradeVermin(value: VerminGradingScale?) {
        setFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbGradeVermin"))
    }

    /**
     * climb_grade_font: what climb_grade_value means when this message selects it.
     */
    public val climbGradeFont: FontGradingScale?
        get() = (getFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbGradeFont")) as? UByte)?.let { FontGradingScale.fromValue(it) }

    public fun setClimbGradeFont(value: FontGradingScale?) {
        setFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbGradeFont"))
    }

    /**
     * climb_grade_dankyu: what climb_grade_value means when this message selects it.
     */
    public val climbGradeDankyu: DankyuGradingScale?
        get() = (getFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, 0, SubFieldSelector.Named("ClimbGradeDankyu")) as? UByte)?.let { DankyuGradingScale.fromValue(it) }

    public fun setClimbGradeDankyu(value: DankyuGradingScale?) {
        setFieldValue(CLIMB_GRADE_VALUE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("ClimbGradeDankyu"))
    }

    /** status. Use with indoor climbing activities only */
    public var status: SplitStatus?
        get() = (getFieldValue(STATUS_FIELD_NUM) as? UByte)?.let { SplitStatus.fromValue(it) }
        set(value) = setFieldValue(STATUS_FIELD_NUM, value?.value)

    /** num_falls. Use with indoor climbing activities only */
    public var numFalls: UShort?
        get() = getFieldValue(NUM_FALLS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(NUM_FALLS_FIELD_NUM, value)

    /** climb_send. Use with indoor climbing activities only */
    public var climbSend: Boolean?
        get() = (getFieldValue(CLIMB_SEND_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(CLIMB_SEND_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** start_elevation, in m. */
    public var startElevation: Double?
        get() = getFieldValue(START_ELEVATION_FIELD_NUM) as? Double
        set(value) = setFieldValue(START_ELEVATION_FIELD_NUM, value)

    /** active_time, in s. Active time of split rounds */
    public var activeTime: Double?
        get() = getFieldValue(ACTIVE_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(ACTIVE_TIME_FIELD_NUM, value)

    /** metabolic_calories, in kcal. */
    public var metabolicCalories: UShort?
        get() = getFieldValue(METABOLIC_CALORIES_FIELD_NUM) as? UShort
        set(value) = setFieldValue(METABOLIC_CALORIES_FIELD_NUM, value)

    /** total_fractional_ascent, in m. fractional part of total_ascent */
    public var totalFractionalAscent: Double?
        get() = getFieldValue(TOTAL_FRACTIONAL_ASCENT_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_FRACTIONAL_ASCENT_FIELD_NUM, value)

    /** total_fractional_descent, in m. fractional part of total_descent */
    public var totalFractionalDescent: Double?
        get() = getFieldValue(TOTAL_FRACTIONAL_DESCENT_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_FRACTIONAL_DESCENT_FIELD_NUM, value)

    /** avg_grade, in %. */
    public var avgGrade: Double?
        get() = getFieldValue(AVG_GRADE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_GRADE_FIELD_NUM, value)

    /** max_grade, in %. */
    public var maxGrade: Double?
        get() = getFieldValue(MAX_GRADE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_GRADE_FIELD_NUM, value)

    /** min_cadence, in rpm. */
    public var minCadence: Double?
        get() = getFieldValue(MIN_CADENCE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MIN_CADENCE_FIELD_NUM, value)

    /**
     * min_running_cadence: what min_cadence means when this message selects it.
     */
    public val minRunningCadence: Double?
        get() = getFieldValue(MIN_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("MinRunningCadence")) as? Double

    public fun setMinRunningCadence(value: Double?) {
        setFieldValue(MIN_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("MinRunningCadence"))
    }

    /**
     * min_swimming_cadence: what min_cadence means when this message selects it.
     */
    public val minSwimmingCadence: Double?
        get() = getFieldValue(MIN_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("MinSwimmingCadence")) as? Double

    public fun setMinSwimmingCadence(value: Double?) {
        setFieldValue(MIN_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("MinSwimmingCadence"))
    }

    /**
     * min_paddlesport_cadence: what min_cadence means when this message selects it.
     */
    public val minPaddlesportCadence: Double?
        get() = getFieldValue(MIN_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("MinPaddlesportCadence")) as? Double

    public fun setMinPaddlesportCadence(value: Double?) {
        setFieldValue(MIN_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("MinPaddlesportCadence"))
    }

    /**
     * min_push_cadence: what min_cadence means when this message selects it.
     *
     * min pushes per minute in a split
     */
    public val minPushCadence: UShort?
        get() = getFieldValue(MIN_CADENCE_FIELD_NUM, 0, SubFieldSelector.Named("MinPushCadence")) as? UShort

    public fun setMinPushCadence(value: UShort?) {
        setFieldValue(MIN_CADENCE_FIELD_NUM, value, 0, SubFieldSelector.Named("MinPushCadence"))
    }

    /** avg_grade_adjusted_speed, in m/s. */
    public var avgGradeAdjustedSpeed: Double?
        get() = getFieldValue(AVG_GRADE_ADJUSTED_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_GRADE_ADJUSTED_SPEED_FIELD_NUM, value)

    /** avg_stress. */
    public var avgStress: UShort?
        get() = getFieldValue(AVG_STRESS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(AVG_STRESS_FIELD_NUM, value)

    /** avg_vam, in m/s. */
    public var avgVam: Double?
        get() = getFieldValue(AVG_VAM_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_VAM_FIELD_NUM, value)

    /** jump_count. */
    public var jumpCount: UByte?
        get() = getFieldValue(JUMP_COUNT_FIELD_NUM) as? UByte
        set(value) = setFieldValue(JUMP_COUNT_FIELD_NUM, value)

    /** total_moving_time, in s. */
    public var totalMovingTime: Double?
        get() = getFieldValue(TOTAL_MOVING_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_MOVING_TIME_FIELD_NUM, value)

    /** dive_section_type. */
    public var diveSectionType: DiveSectionType?
        get() = (getFieldValue(DIVE_SECTION_TYPE_FIELD_NUM) as? UByte)?.let { DiveSectionType.fromValue(it) }
        set(value) = setFieldValue(DIVE_SECTION_TYPE_FIELD_NUM, value?.value)

    /** avg_ascent_rate, in m/s. */
    public var avgAscentRate: Double?
        get() = getFieldValue(AVG_ASCENT_RATE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_ASCENT_RATE_FIELD_NUM, value)

    /** max_ascent_rate, in m/s. */
    public var maxAscentRate: Double?
        get() = getFieldValue(MAX_ASCENT_RATE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_ASCENT_RATE_FIELD_NUM, value)

    /** avg_descent_rate, in m/s. */
    public var avgDescentRate: Double?
        get() = getFieldValue(AVG_DESCENT_RATE_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_DESCENT_RATE_FIELD_NUM, value)

    /** max_descent_rate, in m/s. */
    public var maxDescentRate: Double?
        get() = getFieldValue(MAX_DESCENT_RATE_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_DESCENT_RATE_FIELD_NUM, value)

    /** total_ascent_time, in s. */
    public var totalAscentTime: Double?
        get() = getFieldValue(TOTAL_ASCENT_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_ASCENT_TIME_FIELD_NUM, value)

    /** total_descent_time, in s. */
    public var totalDescentTime: Double?
        get() = getFieldValue(TOTAL_DESCENT_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_DESCENT_TIME_FIELD_NUM, value)

    /** total_hang_time, in s. */
    public var totalHangTime: Double?
        get() = getFieldValue(TOTAL_HANG_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_HANG_TIME_FIELD_NUM, value)

    /** apnea_discipline. */
    public var apneaDiscipline: ApneaDiscipline?
        get() = (getFieldValue(APNEA_DISCIPLINE_FIELD_NUM) as? UByte)?.let { ApneaDiscipline.fromValue(it) }
        set(value) = setFieldValue(APNEA_DISCIPLINE_FIELD_NUM, value?.value)

    /** avg_depth, in m. Average depth. 0 if above water */
    public var avgDepth: Double?
        get() = getFieldValue(AVG_DEPTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_DEPTH_FIELD_NUM, value)

    /** max_depth, in m. Max depth. 0 if above water */
    public var maxDepth: Double?
        get() = getFieldValue(MAX_DEPTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(MAX_DEPTH_FIELD_NUM, value)

    /** min_heart_rate, in bpm. */
    public var minHeartRate: UByte?
        get() = getFieldValue(MIN_HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(MIN_HEART_RATE_FIELD_NUM, value)

    /** surface_interval, in s. Time since end of last dive */
    public var surfaceInterval: UInt?
        get() = getFieldValue(SURFACE_INTERVAL_FIELD_NUM) as? UInt
        set(value) = setFieldValue(SURFACE_INTERVAL_FIELD_NUM, value)

    /** total_fractional_cycles, in cycles. */
    public var totalFractionalCycles: Double?
        get() = getFieldValue(TOTAL_FRACTIONAL_CYCLES_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_FRACTIONAL_CYCLES_FIELD_NUM, value)

    /** avg_stance_time_percent, in percent. */
    public var avgStanceTimePercent: Double?
        get() = getFieldValue(AVG_STANCE_TIME_PERCENT_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_STANCE_TIME_PERCENT_FIELD_NUM, value)

    /** total_anaerobic_training_effect. */
    public var totalAnaerobicTrainingEffect: Double?
        get() = getFieldValue(TOTAL_ANAEROBIC_TRAINING_EFFECT_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_ANAEROBIC_TRAINING_EFFECT_FIELD_NUM, value)

    /** front_gear_shift_count. */
    public var frontGearShiftCount: UShort?
        get() = getFieldValue(FRONT_GEAR_SHIFT_COUNT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(FRONT_GEAR_SHIFT_COUNT_FIELD_NUM, value)

    /** rear_gear_shift_count. */
    public var rearGearShiftCount: UShort?
        get() = getFieldValue(REAR_GEAR_SHIFT_COUNT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(REAR_GEAR_SHIFT_COUNT_FIELD_NUM, value)

    public companion object {
        public const val MESSAGE_INDEX_FIELD_NUM: UByte = 254u
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val SPLIT_TYPE_FIELD_NUM: UByte = 0u
        public const val TOTAL_ELAPSED_TIME_FIELD_NUM: UByte = 1u
        public const val TOTAL_TIMER_TIME_FIELD_NUM: UByte = 2u
        public const val TOTAL_DISTANCE_FIELD_NUM: UByte = 3u
        public const val AVG_SPEED_FIELD_NUM: UByte = 4u
        public const val START_TIME_FIELD_NUM: UByte = 9u
        public const val SPORT_FIELD_NUM: UByte = 11u
        public const val SUB_SPORT_FIELD_NUM: UByte = 12u
        public const val TOTAL_ASCENT_FIELD_NUM: UByte = 13u
        public const val TOTAL_DESCENT_FIELD_NUM: UByte = 14u
        public const val AVG_HEART_RATE_FIELD_NUM: UByte = 15u
        public const val MAX_HEART_RATE_FIELD_NUM: UByte = 16u
        public const val NEC_LAT_FIELD_NUM: UByte = 17u
        public const val NEC_LONG_FIELD_NUM: UByte = 18u
        public const val SWC_LAT_FIELD_NUM: UByte = 19u
        public const val SWC_LONG_FIELD_NUM: UByte = 20u
        public const val START_POSITION_LAT_FIELD_NUM: UByte = 21u
        public const val START_POSITION_LONG_FIELD_NUM: UByte = 22u
        public const val END_POSITION_LAT_FIELD_NUM: UByte = 23u
        public const val END_POSITION_LONG_FIELD_NUM: UByte = 24u
        public const val MAX_SPEED_FIELD_NUM: UByte = 25u
        public const val AVG_VERT_SPEED_FIELD_NUM: UByte = 26u
        public const val END_TIME_FIELD_NUM: UByte = 27u
        public const val TOTAL_CALORIES_FIELD_NUM: UByte = 28u
        public const val AVG_CADENCE_FIELD_NUM: UByte = 29u
        public const val MAX_CADENCE_FIELD_NUM: UByte = 30u
        public const val TOTAL_CYCLES_FIELD_NUM: UByte = 31u
        public const val AVG_TEMPERATURE_FIELD_NUM: UByte = 32u
        public const val MAX_TEMPERATURE_FIELD_NUM: UByte = 33u
        public const val MIN_TEMPERATURE_FIELD_NUM: UByte = 34u
        public const val AVG_VERTICAL_OSCILLATION_FIELD_NUM: UByte = 35u
        public const val AVG_VERTICAL_RATIO_FIELD_NUM: UByte = 36u
        public const val AVG_STANCE_TIME_FIELD_NUM: UByte = 37u
        public const val AVG_STANCE_TIME_BALANCE_FIELD_NUM: UByte = 38u
        public const val AVG_STEP_LENGTH_FIELD_NUM: UByte = 39u
        public const val AVG_POWER_FIELD_NUM: UByte = 40u
        public const val MAX_POWER_FIELD_NUM: UByte = 41u
        public const val NORMALIZED_POWER_FIELD_NUM: UByte = 42u
        public const val LEFT_RIGHT_BALANCE_FIELD_NUM: UByte = 43u
        public const val TIME_STANDING_FIELD_NUM: UByte = 44u
        public const val AVG_LEFT_PCO_FIELD_NUM: UByte = 45u
        public const val AVG_RIGHT_PCO_FIELD_NUM: UByte = 46u
        public const val AVG_LEFT_POWER_PHASE_FIELD_NUM: UByte = 47u
        public const val AVG_LEFT_POWER_PHASE_PEAK_FIELD_NUM: UByte = 48u
        public const val AVG_RIGHT_POWER_PHASE_FIELD_NUM: UByte = 49u
        public const val AVG_RIGHT_POWER_PHASE_PEAK_FIELD_NUM: UByte = 50u
        public const val AVG_POWER_POSITION_FIELD_NUM: UByte = 51u
        public const val MAX_POWER_POSITION_FIELD_NUM: UByte = 52u
        public const val AVG_LEFT_TORQUE_EFFECTIVENESS_FIELD_NUM: UByte = 53u
        public const val AVG_RIGHT_TORQUE_EFFECTIVENESS_FIELD_NUM: UByte = 54u
        public const val AVG_LEFT_PEDAL_SMOOTHNESS_FIELD_NUM: UByte = 55u
        public const val AVG_RIGHT_PEDAL_SMOOTHNESS_FIELD_NUM: UByte = 56u
        public const val AVG_COMBINED_PEDAL_SMOOTHNESS_FIELD_NUM: UByte = 57u
        public const val AVG_FLOW_FIELD_NUM: UByte = 58u
        public const val TOTAL_GRIT_FIELD_NUM: UByte = 59u
        public const val SWIM_STROKE_FIELD_NUM: UByte = 62u
        public const val NUM_ACTIVE_LENGTHS_FIELD_NUM: UByte = 63u
        public const val AVG_SWOLF_FIELD_NUM: UByte = 64u
        public const val AVG_STROKE_DISTANCE_FIELD_NUM: UByte = 65u
        public const val AVG_STROKES_PER_LENGTH_FIELD_NUM: UByte = 66u
        public const val FIRST_LAP_INDEX_FIELD_NUM: UByte = 67u
        public const val NUM_LAPS_FIELD_NUM: UByte = 68u
        public const val CLIMB_GRADING_SCALE_FIELD_NUM: UByte = 69u
        public const val CLIMB_GRADE_VALUE_FIELD_NUM: UByte = 70u
        public const val STATUS_FIELD_NUM: UByte = 71u
        public const val NUM_FALLS_FIELD_NUM: UByte = 72u
        public const val CLIMB_SEND_FIELD_NUM: UByte = 73u
        public const val START_ELEVATION_FIELD_NUM: UByte = 74u
        public const val ACTIVE_TIME_FIELD_NUM: UByte = 78u
        public const val METABOLIC_CALORIES_FIELD_NUM: UByte = 79u
        public const val TOTAL_FRACTIONAL_ASCENT_FIELD_NUM: UByte = 80u
        public const val TOTAL_FRACTIONAL_DESCENT_FIELD_NUM: UByte = 81u
        public const val AVG_GRADE_FIELD_NUM: UByte = 88u
        public const val MAX_GRADE_FIELD_NUM: UByte = 89u
        public const val MIN_CADENCE_FIELD_NUM: UByte = 90u
        public const val AVG_GRADE_ADJUSTED_SPEED_FIELD_NUM: UByte = 93u
        public const val AVG_STRESS_FIELD_NUM: UByte = 94u
        public const val AVG_VAM_FIELD_NUM: UByte = 99u
        public const val JUMP_COUNT_FIELD_NUM: UByte = 104u
        public const val TOTAL_MOVING_TIME_FIELD_NUM: UByte = 110u
        public const val DIVE_SECTION_TYPE_FIELD_NUM: UByte = 112u
        public const val AVG_ASCENT_RATE_FIELD_NUM: UByte = 113u
        public const val MAX_ASCENT_RATE_FIELD_NUM: UByte = 114u
        public const val AVG_DESCENT_RATE_FIELD_NUM: UByte = 115u
        public const val MAX_DESCENT_RATE_FIELD_NUM: UByte = 116u
        public const val TOTAL_ASCENT_TIME_FIELD_NUM: UByte = 117u
        public const val TOTAL_DESCENT_TIME_FIELD_NUM: UByte = 118u
        public const val TOTAL_HANG_TIME_FIELD_NUM: UByte = 119u
        public const val APNEA_DISCIPLINE_FIELD_NUM: UByte = 120u
        public const val AVG_DEPTH_FIELD_NUM: UByte = 121u
        public const val MAX_DEPTH_FIELD_NUM: UByte = 122u
        public const val MIN_HEART_RATE_FIELD_NUM: UByte = 124u
        public const val SURFACE_INTERVAL_FIELD_NUM: UByte = 127u
        public const val TOTAL_FRACTIONAL_CYCLES_FIELD_NUM: UByte = 142u
        public const val AVG_STANCE_TIME_PERCENT_FIELD_NUM: UByte = 144u
        public const val TOTAL_ANAEROBIC_TRAINING_EFFECT_FIELD_NUM: UByte = 168u
        public const val FRONT_GEAR_SHIFT_COUNT_FIELD_NUM: UByte = 169u
        public const val REAR_GEAR_SHIFT_COUNT_FIELD_NUM: UByte = 170u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[254] = Field("MessageIndex", 254u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("SplitType", 0u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[1] = Field("TotalElapsedTime", 1u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[2] = Field("TotalTimerTime", 2u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[3] = Field("TotalDistance", 3u, BaseType.UINT32, 100.0, 0.0, "m", false)
            fields[4] = Field("AvgSpeed", 4u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            fields[9] = Field("StartTime", 9u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[11] = Field("Sport", 11u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[12] = Field("SubSport", 12u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[13] = Field("TotalAscent", 13u, BaseType.UINT16, 1.0, 0.0, "m", false)
            fields[14] = Field("TotalDescent", 14u, BaseType.UINT16, 1.0, 0.0, "m", false)
            fields[15] = Field("AvgHeartRate", 15u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[16] = Field("MaxHeartRate", 16u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[17] = Field("NecLat", 17u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[18] = Field("NecLong", 18u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[19] = Field("SwcLat", 19u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[20] = Field("SwcLong", 20u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[21] = Field("StartPositionLat", 21u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[22] = Field("StartPositionLong", 22u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[23] = Field("EndPositionLat", 23u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[24] = Field("EndPositionLong", 24u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[25] = Field("MaxSpeed", 25u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            fields[26] = Field("AvgVertSpeed", 26u, BaseType.SINT32, 1000.0, 0.0, "m/s", false)
            fields[27] = Field("EndTime", 27u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[28] = Field("TotalCalories", 28u, BaseType.UINT32, 1.0, 0.0, "kcal", false)
            fields[29] = Field("AvgCadence", 29u, BaseType.UINT16, 128.0, 0.0, "rpm", false)
                .addSubField(
                    SubField("AvgRunningCadence", BaseType.UINT16, 128.0, 0.0, "strides/min").also { subField ->
                        subField.addMap(11u, 1)
                    },
                )
                .addSubField(
                    SubField("AvgSwimmingCadence", BaseType.UINT16, 128.0, 0.0, "strokes/min").also { subField ->
                        subField.addMap(11u, 5)
                    },
                )
                .addSubField(
                    SubField("AvgPaddlesportCadence", BaseType.UINT16, 128.0, 0.0, "strokes/min").also { subField ->
                        subField.addMap(11u, 15)
                        subField.addMap(11u, 37)
                    },
                )
                .addSubField(
                    SubField("AvgPushCadence", BaseType.UINT16, 1.0, 0.0, "pushes/min").also { subField ->
                        subField.addMap(11u, 66)
                        subField.addMap(11u, 65)
                    },
                )
            fields[30] = Field("MaxCadence", 30u, BaseType.UINT16, 128.0, 0.0, "rpm", false)
                .addSubField(
                    SubField("MaxRunningCadence", BaseType.UINT16, 128.0, 0.0, "strides/min").also { subField ->
                        subField.addMap(11u, 1)
                    },
                )
                .addSubField(
                    SubField("MaxSwimmingCadence", BaseType.UINT16, 128.0, 0.0, "strokes/min").also { subField ->
                        subField.addMap(11u, 5)
                    },
                )
                .addSubField(
                    SubField("MaxPaddlesportCadence", BaseType.UINT16, 128.0, 0.0, "strokes/min").also { subField ->
                        subField.addMap(11u, 15)
                        subField.addMap(11u, 37)
                    },
                )
                .addSubField(
                    SubField("MaxPushCadence", BaseType.UINT16, 1.0, 0.0, "pushes/min").also { subField ->
                        subField.addMap(11u, 66)
                        subField.addMap(11u, 65)
                    },
                )
            fields[31] = Field("TotalCycles", 31u, BaseType.UINT32, 1.0, 0.0, "cycles", false)
                .addSubField(
                    SubField("TotalStrides", BaseType.UINT32, 1.0, 0.0, "strides").also { subField ->
                        subField.addMap(11u, 1)
                        subField.addMap(11u, 11)
                    },
                )
                .addSubField(
                    SubField("TotalStrokes", BaseType.UINT32, 1.0, 0.0, "strokes").also { subField ->
                        subField.addMap(11u, 2)
                        subField.addMap(11u, 5)
                        subField.addMap(11u, 15)
                        subField.addMap(11u, 37)
                    },
                )
                .addSubField(
                    SubField("TotalReps", BaseType.UINT32, 1.0, 0.0, "reps").also { subField ->
                        subField.addMap(11u, 62)
                    },
                )
                .addSubField(
                    SubField("TotalPushes", BaseType.UINT32, 1.0, 0.0, "pushes").also { subField ->
                        subField.addMap(11u, 66)
                        subField.addMap(11u, 65)
                    },
                )
            fields[32] = Field("AvgTemperature", 32u, BaseType.SINT8, 1.0, 0.0, "C", false)
            fields[33] = Field("MaxTemperature", 33u, BaseType.SINT8, 1.0, 0.0, "C", false)
            fields[34] = Field("MinTemperature", 34u, BaseType.SINT8, 1.0, 0.0, "C", false)
            fields[35] = Field("AvgVerticalOscillation", 35u, BaseType.UINT16, 10.0, 0.0, "mm", false)
            fields[36] = Field("AvgVerticalRatio", 36u, BaseType.UINT16, 100.0, 0.0, "percent", false)
            fields[37] = Field("AvgStanceTime", 37u, BaseType.UINT16, 10.0, 0.0, "ms", false)
            fields[38] = Field("AvgStanceTimeBalance", 38u, BaseType.UINT16, 100.0, 0.0, "percent", false)
            fields[39] = Field("AvgStepLength", 39u, BaseType.UINT16, 10.0, 0.0, "mm", false)
            fields[40] = Field("AvgPower", 40u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[41] = Field("MaxPower", 41u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[42] = Field("NormalizedPower", 42u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[43] = Field("LeftRightBalance", 43u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[44] = Field("TimeStanding", 44u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[45] = Field("AvgLeftPco", 45u, BaseType.SINT8, 1.0, 0.0, "mm", false)
            fields[46] = Field("AvgRightPco", 46u, BaseType.SINT8, 1.0, 0.0, "mm", false)
            fields[47] = Field("AvgLeftPowerPhase", 47u, BaseType.UINT8, 0.7111111, 0.0, "degrees", false)
            fields[48] = Field("AvgLeftPowerPhasePeak", 48u, BaseType.UINT8, 0.7111111, 0.0, "degrees", false)
            fields[49] = Field("AvgRightPowerPhase", 49u, BaseType.UINT8, 0.7111111, 0.0, "degrees", false)
            fields[50] = Field("AvgRightPowerPhasePeak", 50u, BaseType.UINT8, 0.7111111, 0.0, "degrees", false)
            fields[51] = Field("AvgPowerPosition", 51u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[52] = Field("MaxPowerPosition", 52u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[53] = Field("AvgLeftTorqueEffectiveness", 53u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[54] = Field("AvgRightTorqueEffectiveness", 54u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[55] = Field("AvgLeftPedalSmoothness", 55u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[56] = Field("AvgRightPedalSmoothness", 56u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[57] = Field("AvgCombinedPedalSmoothness", 57u, BaseType.UINT8, 2.0, 0.0, "percent", false)
            fields[58] = Field("AvgFlow", 58u, BaseType.FLOAT32, 1.0, 0.0, "Flow", false)
            fields[59] = Field("TotalGrit", 59u, BaseType.FLOAT32, 1.0, 0.0, "kGrit", false)
            fields[62] = Field("SwimStroke", 62u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[63] = Field("NumActiveLengths", 63u, BaseType.UINT16, 1.0, 0.0, "lengths", false)
            fields[64] = Field("AvgSwolf", 64u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[65] = Field("AvgStrokeDistance", 65u, BaseType.UINT16, 100.0, 0.0, "m", false)
            fields[66] = Field("AvgStrokesPerLength", 66u, BaseType.UINT16, 10.0, 0.0, "strokes / length", false)
            fields[67] = Field("FirstLapIndex", 67u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[68] = Field("NumLaps", 68u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[69] = Field("ClimbGradingScale", 69u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[70] = Field("ClimbGradeValue", 70u, BaseType.UINT32, 1.0, 0.0, "", false)
                .addSubField(
                    SubField("ClimbGradeYds", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(69u, 0)
                    },
                )
                .addSubField(
                    SubField("ClimbGradeUiaa", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(69u, 1)
                    },
                )
                .addSubField(
                    SubField("ClimbGradeFrench", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(69u, 2)
                    },
                )
                .addSubField(
                    SubField("ClimbGradeBritishAdjectival", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(69u, 3)
                    },
                )
                .addSubField(
                    SubField("ClimbGradeBritishTechnical", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(69u, 4)
                    },
                )
                .addSubField(
                    SubField("ClimbGradeEwbank", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(69u, 5)
                    },
                )
                .addSubField(
                    SubField("ClimbGradeBrazilian", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(69u, 6)
                    },
                )
                .addSubField(
                    SubField("ClimbGradeSaxon", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(69u, 7)
                    },
                )
                .addSubField(
                    SubField("ClimbGradeVermin", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(69u, 8)
                    },
                )
                .addSubField(
                    SubField("ClimbGradeFont", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(69u, 9)
                    },
                )
                .addSubField(
                    SubField("ClimbGradeDankyu", BaseType.ENUM, 1.0, 0.0, "").also { subField ->
                        subField.addMap(69u, 10)
                    },
                )
            fields[71] = Field("Status", 71u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[72] = Field("NumFalls", 72u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[73] = Field("ClimbSend", 73u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[74] = Field("StartElevation", 74u, BaseType.UINT32, 5.0, 500.0, "m", false)
            fields[78] = Field("ActiveTime", 78u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[79] = Field("MetabolicCalories", 79u, BaseType.UINT16, 1.0, 0.0, "kcal", false)
            fields[80] = Field("TotalFractionalAscent", 80u, BaseType.UINT8, 100.0, 0.0, "m", false)
            fields[81] = Field("TotalFractionalDescent", 81u, BaseType.UINT8, 100.0, 0.0, "m", false)
            fields[88] = Field("AvgGrade", 88u, BaseType.SINT16, 100.0, 0.0, "%", false)
            fields[89] = Field("MaxGrade", 89u, BaseType.SINT16, 100.0, 0.0, "%", false)
            fields[90] = Field("MinCadence", 90u, BaseType.UINT16, 128.0, 0.0, "rpm", false)
                .addSubField(
                    SubField("MinRunningCadence", BaseType.UINT16, 128.0, 0.0, "strides/min").also { subField ->
                        subField.addMap(11u, 1)
                    },
                )
                .addSubField(
                    SubField("MinSwimmingCadence", BaseType.UINT16, 128.0, 0.0, "strokes/min").also { subField ->
                        subField.addMap(11u, 5)
                    },
                )
                .addSubField(
                    SubField("MinPaddlesportCadence", BaseType.UINT16, 128.0, 0.0, "strokes/min").also { subField ->
                        subField.addMap(11u, 15)
                        subField.addMap(11u, 37)
                    },
                )
                .addSubField(
                    SubField("MinPushCadence", BaseType.UINT16, 1.0, 0.0, "pushes/min").also { subField ->
                        subField.addMap(11u, 66)
                        subField.addMap(11u, 65)
                    },
                )
            fields[93] = Field("AvgGradeAdjustedSpeed", 93u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            fields[94] = Field("AvgStress", 94u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[99] = Field("AvgVam", 99u, BaseType.UINT16, 1000.0, 0.0, "m/s", false)
            fields[104] = Field("JumpCount", 104u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[110] = Field("TotalMovingTime", 110u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[112] = Field("DiveSectionType", 112u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[113] = Field("AvgAscentRate", 113u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            fields[114] = Field("MaxAscentRate", 114u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            fields[115] = Field("AvgDescentRate", 115u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            fields[116] = Field("MaxDescentRate", 116u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            fields[117] = Field("TotalAscentTime", 117u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[118] = Field("TotalDescentTime", 118u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[119] = Field("TotalHangTime", 119u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[120] = Field("ApneaDiscipline", 120u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[121] = Field("AvgDepth", 121u, BaseType.UINT32, 1000.0, 0.0, "m", false)
            fields[122] = Field("MaxDepth", 122u, BaseType.UINT32, 1000.0, 0.0, "m", false)
            fields[124] = Field("MinHeartRate", 124u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[127] = Field("SurfaceInterval", 127u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[142] = Field("TotalFractionalCycles", 142u, BaseType.UINT8, 128.0, 0.0, "cycles", false)
            fields[144] = Field("AvgStanceTimePercent", 144u, BaseType.UINT16, 100.0, 0.0, "percent", false)
            fields[168] = Field("TotalAnaerobicTrainingEffect", 168u, BaseType.UINT8, 10.0, 0.0, "", false)
            fields[169] = Field("FrontGearShiftCount", 169u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[170] = Field("RearGearShiftCount", 170u, BaseType.UINT16, 1.0, 0.0, "", false)
            return fields
        }
    }
}
